import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoomByPath } from '@/constants/rooms';
import { useGameStore } from '@/stores/gameStore';
import { getLevelProgress } from '@/constants/gamification';
import { slideInRight, useReducedMotion } from '@/lib/animations';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { WidgetLayoutItem } from './bento-types';
import './RightSidebar.css';

type QuickAction = {
  id: string;
  label: string;
  icon: string;
  to: string;
};

type SidebarNotification = {
  id: string;
  text: string;
  at: string;
  linkTo: string;
  isRead: boolean;
};

type AiInboxItem = {
  id: string;
  title: string;
  subtitle: string;
  linkTo: string;
};

const QUICK_ACTIONS: Record<string, QuickAction[]> = {
  dashboard: [
    { id: 'new-task', label: 'New Task', icon: '📋', to: '/' },
    { id: 'check-in', label: 'Check In', icon: '☀️', to: '/' },
    { id: 'open-journal', label: 'Open Journal', icon: '✍️', to: '/journal' },
  ],
  health: [
    { id: 'log-mood', label: 'Log Mood', icon: '😊', to: '/health' },
    { id: 'add-habit', label: 'Add Habit', icon: '🌱', to: '/health' },
    { id: 'start-breathing', label: 'Start Breathing', icon: '🫁', to: '/health' },
  ],
  wealth: [
    { id: 'add-transaction', label: 'Add Transaction', icon: '💸', to: '/wealth' },
    { id: 'new-invoice', label: 'New Invoice', icon: '🧾', to: '/business' },
    { id: 'add-goal', label: 'Add Goal', icon: '🎯', to: '/wealth' },
  ],
  business: [
    { id: 'new-lead', label: 'New Lead', icon: '🎯', to: '/business' },
    { id: 'new-client', label: 'New Client', icon: '🤝', to: '/business' },
    { id: 'create-quote', label: 'Create Quote', icon: '💼', to: '/business' },
  ],
  journal: [
    { id: 'new-note', label: 'New Note', icon: '📝', to: '/journal' },
    { id: 'ai-chat', label: 'AI Chat', icon: '🤖', to: '/journal' },
    { id: 'new-template', label: 'New Template', icon: '📄', to: '/journal' },
  ],
  game: [
    { id: 'view-badges', label: 'View Badges', icon: '🏅', to: '/game' },
    { id: 'skill-tree', label: 'Skill Tree', icon: '🌳', to: '/game' },
    { id: 'buy-persona', label: 'Buy Persona', icon: '🧠', to: '/game' },
  ],
};

const STRIP_SECTIONS = [
  { id: 'game', icon: '🎮', label: 'Game bar' },
  { id: 'notifications', icon: '🔔', label: 'Notifications' },
  { id: 'inbox', icon: '📥', label: 'AI Inbox' },
  { id: 'hidden', icon: '📌', label: 'Hidden widgets' },
  { id: 'actions', icon: '⚡', label: 'Quick actions' },
  { id: 'mods', icon: '🧩', label: 'Modular zone' },
];

const WIDGET_SUMMARIES: Record<string, string> = {
  'daily-briefing': '🧠 Briefing summary hidden',
  'focus-tasks': '📋 Focus tasks hidden',
  'daily-tarot': '🔮 Tarot hidden',
  'streak-wall': '🔥 Streak hidden',
  'scratch-pad': '📝 Scratch pad hidden',
  'quick-actions': '⚡ Quick actions hidden',
};

export function RightSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const gameState = useGameStore((state) => state.gameState);
  const user = useAuthStore((state) => state.user);
  const settings = useAuthStore((state) => state.settings);
  const room = getRoomByPath(location.pathname);
  const roomId = room?.id ?? 'dashboard';
  const actions: QuickAction[] =
    QUICK_ACTIONS[roomId as keyof typeof QUICK_ACTIONS] ?? QUICK_ACTIONS.dashboard ?? [];

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1280 : false,
  );
  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('johnicorn:right-sidebar:pinned') !== '0';
  });
  const [open, setOpen] = useState(false);
  const [hiddenWidgets, setHiddenWidgets] = useState<WidgetLayoutItem[]>([]);
  const [notifications, setNotifications] = useState<SidebarNotification[]>([]);
  const [aiInbox, setAiInbox] = useState<AiInboxItem[]>([]);
  const [enabledModules, setEnabledModules] = useState<string[]>([]);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1280);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    window.localStorage.setItem('johnicorn:right-sidebar:pinned', pinned ? '1' : '0');
  }, [pinned]);

  useEffect(() => {
    if (isNarrow) setOpen(false);
  }, [isNarrow, location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const loadHidden = async () => {
      const row = await supabase
        .from('widget_layouts')
        .select('layout')
        .eq('user_id', user.id)
        .eq('room', roomId)
        .maybeSingle();
      if (!mounted) return;
      const layout = Array.isArray(row.data?.layout) ? (row.data.layout as WidgetLayoutItem[]) : [];
      setHiddenWidgets(layout.filter((item) => item.hidden));
    };
    void loadHidden();
    const onLayoutUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ roomId?: string }>).detail;
      if (!detail?.roomId || detail.roomId === roomId) {
        void loadHidden();
      }
    };
    window.addEventListener('johnicorn:widget-layout-updated', onLayoutUpdated as EventListener);
    return () => {
      mounted = false;
      window.removeEventListener('johnicorn:widget-layout-updated', onLayoutUpdated as EventListener);
    };
  }, [roomId, user]);

  useEffect(() => {
    const fromSettings = settings?.theme_json?.sidebarModules;
    if (typeof fromSettings === 'string' && fromSettings.trim().length > 0) {
      const parsed = fromSettings.split(',').map((value) => value.trim()).filter(Boolean);
      setEnabledModules(parsed);
      window.localStorage.setItem('johnicorn:right-sidebar:modules', JSON.stringify(parsed));
      return;
    }
    const raw = window.localStorage.getItem('johnicorn:right-sidebar:modules');
    const parsed = raw ? (JSON.parse(raw) as string[]) : ['daily_challenge', 'upcoming_deadlines'];
    setEnabledModules(Array.isArray(parsed) ? parsed : ['daily_challenge', 'upcoming_deadlines']);
  }, [settings?.theme_json?.sidebarModules]);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    void (async () => {
      const [messagesRes, cacheRes] = await Promise.all([
        supabase
          .from('messages')
          .select('id,subject,created_at,is_read')
          .eq('recipient_id', user.id)
          .order('created_at', { ascending: false })
          .limit(8),
        supabase
          .from('ai_cache')
          .select('id,feature,cache_key,updated_at')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(8),
      ]);
      if (!mounted) return;
      const notif: SidebarNotification[] = (messagesRes.data ?? []).map((row) => ({
        id: row.id,
        text: row.subject,
        at: row.created_at,
        linkTo: '/',
        isRead: row.is_read,
      }));
      setNotifications(notif);
      const inbox: AiInboxItem[] = (cacheRes.data ?? []).map((row) => ({
        id: row.id,
        title: mapFeatureTitle(row.feature),
        subtitle: `Updated ${relativeTime(row.updated_at)}`,
        linkTo: mapFeatureRoute(row.feature),
      }));
      setAiInbox(inbox);
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  async function restoreHiddenWidget(widgetId: string) {
    if (!user) return;
    const row = await supabase
      .from('widget_layouts')
      .select('layout')
      .eq('user_id', user.id)
      .eq('room', roomId)
      .maybeSingle();
    const layout = Array.isArray(row.data?.layout) ? (row.data.layout as WidgetLayoutItem[]) : [];
    const next = layout.map((item) =>
      item.widgetId === widgetId ? { ...item, hidden: false } : item,
    );
    await supabase
      .from('widget_layouts')
      .upsert({ user_id: user.id, room: roomId, layout: next as unknown as never }, { onConflict: 'user_id,room' });
    setHiddenWidgets(next.filter((item) => item.hidden));
  }

  const levelProgress = useMemo(
    () => getLevelProgress(gameState?.total_xp ?? 0),
    [gameState?.total_xp],
  );

  const shouldCollapse = isNarrow || !pinned;

  return (
    <>
      {shouldCollapse ? (
        <aside className="right-sidebar-strip" aria-label="Right sidebar controls">
          <button
            type="button"
            className="right-sidebar-strip__toggle"
            onClick={() => setOpen((value) => !value)}
            aria-label="Toggle right sidebar"
          >
            {open ? '→' : '←'}
          </button>
          {STRIP_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className="right-sidebar-strip__icon"
              onClick={() => setOpen(true)}
              aria-label={section.label}
              title={section.label}
            >
              {section.icon}
            </button>
          ))}
        </aside>
      ) : (
        <aside className="right-sidebar">
          <SidebarSections
            levelProgress={levelProgress}
            gameState={gameState}
            actions={actions}
            onActionClick={(to) => navigate(to)}
            roomLabel={room?.label ?? 'Dashboard'}
            onUnpin={() => setPinned(false)}
            showPinButton
            hiddenWidgets={hiddenWidgets}
            onRestoreHidden={restoreHiddenWidget}
            notifications={notifications}
            aiInbox={aiInbox}
            enabledModules={enabledModules}
          />
        </aside>
      )}

      <AnimatePresence>
        {shouldCollapse && open && (
          <>
            <motion.div
              className="right-sidebar-overlay-backdrop"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.2 }}
            />
            <motion.aside
              className="right-sidebar right-sidebar--overlay"
              variants={slideInRight(reducedMotion)}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <SidebarSections
                levelProgress={levelProgress}
                gameState={gameState}
                actions={actions}
                onActionClick={(to) => {
                  navigate(to);
                  setOpen(false);
                }}
                roomLabel={room?.label ?? 'Dashboard'}
                onUnpin={() => setPinned((value) => !value)}
                showPinButton={!isNarrow}
                hiddenWidgets={hiddenWidgets}
                onRestoreHidden={restoreHiddenWidget}
                notifications={notifications}
                aiInbox={aiInbox}
                enabledModules={enabledModules}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

type SidebarSectionsProps = {
  levelProgress: ReturnType<typeof getLevelProgress>;
  gameState: ReturnType<typeof useGameStore.getState>['gameState'];
  actions: QuickAction[];
  roomLabel: string;
  onActionClick: (to: string) => void;
  onUnpin: () => void;
  showPinButton: boolean;
  hiddenWidgets: WidgetLayoutItem[];
  onRestoreHidden: (widgetId: string) => void;
  notifications: SidebarNotification[];
  aiInbox: AiInboxItem[];
  enabledModules: string[];
};

function SidebarSections({
  levelProgress,
  gameState,
  actions,
  roomLabel,
  onActionClick,
  onUnpin,
  showPinButton,
  hiddenWidgets,
  onRestoreHidden,
  notifications,
  aiInbox,
  enabledModules,
}: SidebarSectionsProps) {
  return (
    <>
      <section className="right-sidebar-card right-sidebar-card--sticky">
        <div className="right-sidebar-card__header">
          <h3>Game Bar</h3>
          {showPinButton && (
            <button type="button" className="right-sidebar-pin" onClick={onUnpin} aria-label="Pin or unpin right sidebar">
              📌
            </button>
          )}
        </div>
        <div className="right-sidebar-game">
          <div className="right-sidebar-game__meta">
            <span>Lv {gameState?.current_level ?? 1}</span>
            <span>{gameState?.total_xp ?? 0} XP</span>
          </div>
          <div className="right-sidebar-game__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(levelProgress.percentage)}>
            <div style={{ width: `${levelProgress.percentage}%` }} />
          </div>
          <div className="right-sidebar-game__stats">
            <span>💎 {gameState?.gems_balance ?? 0}</span>
            <span>🔥 {gameState?.current_streak ?? 0}</span>
          </div>
        </div>
      </section>

      <section className="right-sidebar-card">
        <div className="right-sidebar-card__header">
          <h3>Notifications</h3>
          <button
            type="button"
            className="right-sidebar-link-btn"
            onClick={async () => {
              const unread = notifications.filter((item) => !item.isRead).map((item) => item.id);
              if (unread.length > 0) {
                await supabase.from('messages').update({ is_read: true }).in('id', unread);
              }
            }}
          >
            Mark read
          </button>
        </div>
        {notifications.length === 0 ? (
          <p className="right-sidebar-muted">No notifications yet.</p>
        ) : (
          <ul className="right-sidebar-feed">
            {notifications.slice(0, 6).map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => onActionClick(item.linkTo)}>
                  <span>{item.text}</span>
                  <small>{relativeTime(item.at)}</small>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="right-sidebar-card right-sidebar-card--flex">
        <h3>AI Inbox</h3>
        {aiInbox.length === 0 ? (
          <p className="right-sidebar-muted">Coach is watching. Actions will appear here.</p>
        ) : (
          <ul className="right-sidebar-feed">
            {aiInbox.slice(0, 6).map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => onActionClick(item.linkTo)}>
                  <span>{item.title}</span>
                  <small>{item.subtitle}</small>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="right-sidebar-card right-sidebar-card--flex">
        <h3>Hidden Widgets</h3>
        {hiddenWidgets.length === 0 ? (
          <p className="right-sidebar-muted">No hidden widgets in this room.</p>
        ) : (
          <div className="right-sidebar-hidden-list">
            {hiddenWidgets.map((item) => (
              <button key={item.widgetId} type="button" onClick={() => onRestoreHidden(item.widgetId)}>
                {WIDGET_SUMMARIES[item.widgetId] ?? `📌 ${item.widgetId}`}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="right-sidebar-card">
        <h3>Quick Actions · {roomLabel}</h3>
        <div className="right-sidebar-actions">
          {actions.map((action) => (
            <button key={action.id} type="button" onClick={() => onActionClick(action.to)}>
              <span aria-hidden="true">{action.icon}</span>
              {action.label}
            </button>
          ))}
        </div>
      </section>

      <section className="right-sidebar-card right-sidebar-card--flex">
        <h3>Modular Zone</h3>
        {enabledModules.length === 0 ? (
          <p className="right-sidebar-muted">No modules enabled.</p>
        ) : (
          <div className="right-sidebar-modules">
            {enabledModules.includes('daily_challenge') && (
              <div className="right-sidebar-module">🎯 Daily Challenge module</div>
            )}
            {enabledModules.includes('upcoming_deadlines') && (
              <div className="right-sidebar-module">⏳ Upcoming Deadlines module</div>
            )}
            {enabledModules.includes('weather') && (
              <div className="right-sidebar-module">🌤️ Weather: Coming soon</div>
            )}
            {enabledModules.includes('pomodoro') && (
              <div className="right-sidebar-module">🍅 Pomodoro: Coming soon</div>
            )}
          </div>
        )}
      </section>
    </>
  );
}

function mapFeatureTitle(feature: string) {
  if (feature === 'daily_briefing') return 'Daily Briefing draft ready';
  if (feature === 'daily_tarot') return 'Daily Tarot generated';
  if (feature === 'wellness_insights') return 'Wellness insights updated';
  if (feature === 'finance_insights') return 'Finance insights updated';
  return `AI action: ${feature}`;
}

function mapFeatureRoute(feature: string) {
  if (feature.includes('wellness')) return '/health';
  if (feature.includes('finance') || feature.includes('expense')) return '/wealth';
  if (feature.includes('chat') || feature.includes('journal')) return '/journal';
  return '/';
}

function relativeTime(value: string) {
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.max(1, Math.floor(diffMs / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
